import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import {
  CalendarToday,
  Business,
  DirectionsCar,
  Numbers,
  Layers,
  Pets,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { type DERCInfo } from '../types';

interface DercInfoDisplayProps {
  dercInfo: DERCInfo | null;
  productType?: 'A' | 'B' | string | null;
}

const DercInfoDisplay: React.FC<DercInfoDisplayProps> = ({ dercInfo, productType }) => {
  if (!dercInfo) {
    return null;
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: 'action.hover',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
          Información del DERC
        </Typography>

        <Grid container spacing={2}>
          {/* Primera fila */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Fecha de Extracción
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {formatDate(dercInfo.fechaExtraccion)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsCar fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Placa de Vehículo
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {dercInfo.placaVehiculo || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Numbers fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  N° Mallas
                </Typography>
                <Chip
                  label={dercInfo.numeroMallas}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Layers fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  N° Manojos
                </Typography>
                <Chip
                  label={dercInfo.numeroManojos}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>

          {/* Segunda fila */}
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Asociación
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {productType === 'B' ? 'N/A' : (dercInfo.asociacionDescripcion || 'N/A')}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Pets fontSize="small" color="primary" />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Especie
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {dercInfo.especieDescripcion || 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {dercInfo.isEntero ? (
                <CheckCircle fontSize="small" color="success" />
              ) : (
                <Cancel fontSize="small" color="error" />
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Es Entero
                </Typography>
                <Chip
                  label={dercInfo.isEntero ? 'Sí' : 'No'}
                  size="small"
                  color={dercInfo.isEntero ? 'success' : 'error'}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default DercInfoDisplay;